import { Box, Button, Grid, List, ListItem, Typography } from '@mui/material'
import {
  confirmCancellingOrder,
  getPendingCancelledOrders,
  refuseCancellingOrder
} from 'api/daily-order'
import Header from 'components/Header'
import { SnackbarSeverity } from 'constant'
import { SnackContext } from 'contexts/SnackContextProvider'
import useDailyOrder from 'hooks/useDailyOrder'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getPickingBackColor } from 'utils'

const OrderCancellation = () => {
  const { company } = useParams()
  const navigate = useNavigate()

  const [pendingCancelledOrders, setPendingCancelledOrders] = useState<any[]>(
    []
  )
  const [selectedClass, setSelectedClass] = useState<string | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<any>(null)
  const { handleOpenSnackbar } = useContext(SnackContext)
  const { groupAndSortDailyOrdersByClass } = useDailyOrder()

  const fetchPendingCancelledOrders = async () => {
    const result = await getPendingCancelledOrders(`${company}`)
    if (result.data.success) {
      setPendingCancelledOrders(
        groupAndSortDailyOrdersByClass(result.data.orders)
      )
    }
  }

  useEffect(() => {
    fetchPendingCancelledOrders()
  }, [])

  const handleConfirmCancellation = async (orderNumber: string) => {
    try {
      const result = await confirmCancellingOrder(`${company}`, orderNumber)
      if (result.data.success) {
        setPendingCancelledOrders(prev =>
          prev.filter(o => o.orderNumber !== orderNumber)
        )
        setSelectedOrder(null)
        fetchPendingCancelledOrders()
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          `Order ${orderNumber} has been confirmed for cancellation`
        )
      } else {
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          `Failed to confirm cancellation for order ${orderNumber}`
        )
      }
    } catch (error: any) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response?.data?.message ||
          'An error occurred while confirming cancellation'
      )
    }
  }

  const handleRefuseCancellation = async (orderNumber: string) => {
    try {
      const result = await refuseCancellingOrder(`${company}`, orderNumber)
      if (result.data.success) {
        setPendingCancelledOrders(prev =>
          prev.filter(o => o.orderNumber !== orderNumber)
        )
        setSelectedOrder(null)
        fetchPendingCancelledOrders()
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          `Order ${orderNumber} has been refused for cancellation`
        )
      } else {
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          `Failed to refuse cancellation for order ${orderNumber}`
        )
      }
    } catch (error: any) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response?.data?.message ||
          'An error occurred while refusing cancellation'
      )
    }
  }

  return (
    <Box>
      <Box sx={{ m: 2 }}>
        {selectedOrder && (
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid>
              <Button variant='outlined' onClick={() => setSelectedOrder(null)}>
                Back
              </Button>
            </Grid>

            <Grid>
              <Grid container justifyContent='flex-end' gap={2} sx={{ mt: 2 }}>
                <Grid>
                  <Button
                    variant='outlined'
                    color='error'
                    onClick={() =>
                      handleRefuseCancellation(selectedOrder.orderNumber)
                    }
                  >
                    Refuse Cancelling
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() =>
                      handleConfirmCancellation(selectedOrder.orderNumber)
                    }
                  >
                    Confirm Cancelling
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
      {selectedOrder ? (
        <Box sx={{ m: 2 }}>
          <Typography variant='h5' sx={{ mb: 2 }}>
            Order: {selectedOrder.orderNumber}
          </Typography>

          <List>
            {selectedOrder.orderItems.map((item: any) => (
              <ListItem
                key={item._id}
                sx={{
                  display: 'block',
                  backgroundColor: getPickingBackColor(item),
                  mb: 1,
                  borderRadius: 1,
                  p: 1
                }}
              >
                <Grid
                  container
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <Grid>
                    <Typography>{item.name}</Typography>
                    <Typography>Note: {item.note || 'N/A'}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {item.box ? `Box: ${item.box}` : `Bin: ${item.bin}`}
                    </Typography>
                  </Grid>

                  <Grid sx={{ textAlign: 'right' }}>
                    <Typography>Quantity: {item.quantity}</Typography>
                    <Typography>
                      Remaining: {item.quantity - item.quantityPicked}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>
        </Box>
      ) : selectedClass ? (
        <>
          <Box sx={{ m: 2 }}>
            <Button variant='outlined' onClick={() => setSelectedClass(null)}>
              Back
            </Button>
          </Box>

          <List>
            {pendingCancelledOrders
              .find(o => o.class === selectedClass)
              ?.orders.map((order: any) => (
                <ListItem key={order._id}>
                  <Button
                    variant='contained'
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontSize: 26,
                      py: 6,
                      backgroundColor: '#364c63'
                    }}
                    onClick={() => setSelectedOrder(order)}
                  >
                    {order.orderNumber}
                  </Button>
                </ListItem>
              ))}
          </List>
        </>
      ) : pendingCancelledOrders.length > 0 ? (
        <>
          <Header />

          <List>
            {pendingCancelledOrders.map(o => (
              <ListItem key={o.class}>
                <Button
                  variant='contained'
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: 26,
                    py: 6,
                    backgroundColor: '#364c63'
                  }}
                  onClick={() => setSelectedClass(o.class)}
                >
                  {o.class}
                </Button>
              </ListItem>
            ))}
          </List>
        </>
      ) : (
        <Box>
          <Header />
          <Typography variant='h5' sx={{ m: 2 }}>
            No pending cancelled orders
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default OrderCancellation
